import { FORMAT_LABELS, STAGES } from '@jt/core';
import type { ApplicationListItem, CardStatus } from '@jt/shared';
import type { CSSProperties, HTMLAttributes, Ref } from 'react';
import { useAppActions } from '../api/hooks';
import { useOpenDraft } from '../drafts/useDraft';
import { ConfirmButton, useUI } from './overlays';
import { Avatar, Icon, Logo, SourceTag, STAGE_ICON } from './ui';

export function salaryText(a: Pick<ApplicationListItem, 'salaryRange' | 'offer'>) {
  return a.salaryRange || (a.offer?.salaryK ? `€${a.offer.salaryK}k / год` : '');
}

export function cardLine(a: ApplicationListItem) {
  const fmt = a.format
    ? FORMAT_LABELS[a.format] + (a.format !== 'remote' && a.location ? ` (${a.location})` : '')
    : a.location;
  return [salaryText(a), fmt].filter(Boolean).join(' · ');
}

export function CardFoot({ a, ai }: { a: { id: string; card: CardStatus }; ai: boolean }) {
  const openDraft = useOpenDraft();
  const c = a.card;
  switch (c.kind) {
    case 'offer':
      return (
        <div className="cf">
          <span className="chip-ok">
            <Icon name="check" size={14} /> {c.text}
          </span>
          <span>{c.date}</span>
        </div>
      );
    case 'rejected':
      return (
        <div className="cf">
          <span className="l" style={{ color: 'var(--danger)' }}>
            <Icon name="x" size={14} /> {c.text}
          </span>
          <span>{c.date}</span>
        </div>
      );
    case 'closed':
      return (
        <div className="cf">
          <span className="l">
            <Icon name={STAGE_ICON[c.stage]} size={14} /> {c.text}
          </span>
          <span>{c.date}</span>
        </div>
      );
    case 'next':
      return (
        <div className={`chip-date ${c.tone === 'late' ? 'late' : c.tone === 'soon' ? '' : 'v'}`}>
          <Icon name="calendar" size={15} /> {c.text}
        </div>
      );
    case 'reply':
      return ai ? (
        <button
          className="reply-btn"
          onClick={(e) => {
            e.stopPropagation();
            openDraft(a.id, 'reply');
          }}
        >
          Ответить HR <Icon name="chevR" size={15} />
        </button>
      ) : (
        <div className="chip-date v">
          <Icon name="mail" size={15} /> Ждут вашего ответа
        </div>
      );
    case 'silence':
      return (
        <div>
          <span className="chip-warn">
            <Icon name="clock" size={15} /> {c.text}
          </span>
        </div>
      );
    default:
      return (
        <div className="cf">
          <span className="l">
            <Icon name={STAGE_ICON[c.stage]} size={14} /> {c.text}
          </span>
          <span>{c.date}</span>
        </div>
      );
  }
}

/** Меню «⋯» карточки: открыть, перенести в любой статус, удалить. */
export function CardMenu({ a }: { a: ApplicationListItem }) {
  const ui = useUI();
  const act = useAppActions();
  return (
    <>
      <button role="menuitem" onClick={() => ui.openApp(a.id)}>
        <Icon name="ext" size={16} /> Открыть карточку
      </button>
      <div className="mh">Переместить</div>
      {STAGES.filter((s) => s.key !== a.stage).map((s) => (
        <button
          role="menuitem"
          key={s.key}
          onClick={() =>
            act.stage(a.id, s.key).then(
              () => ui.toast(`Перенесено: ${s.title}`),
              () => ui.toast('Не получилось перенести. Повторите.'),
            )
          }
        >
          <span className="cdot" style={{ background: `var(--s-${s.key})` }} />
          {s.title}
        </button>
      ))}
      <hr />
      <DeleteItem id={a.id} />
    </>
  );
}

function DeleteItem({ id }: { id: string }) {
  const ui = useUI();
  const act = useAppActions();
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <ConfirmButton
        className="danger"
        confirmText="Точно удалить?"
        onConfirm={() => {
          ui.closeMenu();
          act.remove(id).then(
            () => ui.toast('Вакансия в корзине — восстановить можно в «Настройках» → «Аккаунт»'),
            () => ui.toast('Не удалилось. Повторите.'),
          );
        }}
      >
        <Icon name="trash" size={16} /> Удалить
      </ConfirmButton>
    </span>
  );
}

export interface CardProps extends HTMLAttributes<HTMLElement> {
  a: ApplicationListItem;
  ai: boolean;
  selected?: boolean;
  dragRef?: Ref<HTMLElement>;
  className?: string;
  style?: CSSProperties;
}

export function AppCard({ a, ai, selected, dragRef, className = '', style, ...rest }: CardProps) {
  const ui = useUI();
  const c = a.contacts[0];
  const line = cardLine(a);
  return (
    <article
      ref={dragRef}
      className={`card ${selected ? 'sel' : ''} ${className}`}
      tabIndex={0}
      aria-label={`${a.role || 'Роль не указана'}, ${a.company || 'Без названия'}`}
      style={style}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button,a')) return;
        ui.openApp(a.id);
      }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          ui.openApp(a.id);
        }
      }}
      {...rest}
    >
      <div className="ch">
        <Logo name={a.company} />
        <div className="ct">
          <b>{a.role || 'Роль не указана'}</b>
          <span>{a.company || 'Без названия'}</span>
        </div>
        <button
          className="icon-btn"
          aria-label="Действия"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            ui.openMenu(e.currentTarget, <CardMenu a={a} />);
          }}
        >
          <Icon name="more" />
        </button>
      </div>
      {line && <div className="cl">{line}</div>}
      {a.source && (
        <div>
          <SourceTag source={a.source} />
        </div>
      )}
      {c && (c.name || c.role || c.email || c.phone || c.telegram) ? (
        <div className="cc">
          <Avatar name={c.name || c.email || c.role} />
          <div className="ccw">
            <span>{[c.name || c.email, c.role].filter(Boolean).join(' · ')}</span>
            {(c.name ? [c.email, c.phone, c.telegram] : [c.phone, c.telegram]).some(Boolean) && (
              <small>
                {(c.name ? [c.email, c.phone, c.telegram] : [c.phone, c.telegram])
                  .filter(Boolean)
                  .join(' · ')}
              </small>
            )}
          </div>
        </div>
      ) : (
        <button
          className="cc-add"
          onClick={(e) => {
            e.stopPropagation();
            ui.openApp(a.id);
          }}
        >
          <Icon name="plus" size={14} /> Добавить HR-контакт
        </button>
      )}
      <CardFoot a={a} ai={ai} />
    </article>
  );
}
